import { api, ApiError } from "./api";

type SessionStatus = { clientId: string; accountId: string; expiresAt: number; serverNow: number };
const activitySignalKey = "calepia:session-activity";
const inputEvents = ["pointerdown", "keydown", "input", "wheel", "touchmove"];
let confirmedClientId = "";
const referenceListeners = new Set<(clientId: string) => void>();
export function authenticationClientReference() { return confirmedClientId; }
export function onAuthenticationClientReference(listener: (clientId: string) => void) {
  referenceListeners.add(listener);
  return () => { referenceListeners.delete(listener); };
}

export function monitorSessionActivity(accountId: string, callbacks: {
  warning: (seconds: number | null) => void;
  expired: () => void;
  changed: () => void;
  error: (message: string) => void;
}) {
  let clientId = "";
  // Bound cached private UI even if the first status request fails after authentication.
  let deadline = performance.now() + 15 * 60_000;
  let lastCheck = 0;
  let lastActivitySent = -Infinity;
  let inputRevision = 0;
  let sentRevision = 0;
  let inFlight = false;
  let stopped = false;

  function signalTabs() {
    try { localStorage.setItem(activitySignalKey, `${clientId}:${Date.now()}`); } catch { /* Status polling also works without storage. */ }
  }

  function expire() {
    if (stopped) return;
    stopped = true;
    signalTabs();
    callbacks.expired();
  }

  async function check(renew = false) {
    if (stopped || inFlight) return;
    inFlight = true;
    const startedAt = performance.now();
    const revision = inputRevision;
    lastCheck = startedAt;
    const sendActivity = renew && Boolean(clientId);
    if (sendActivity) lastActivitySent = startedAt;
    try {
      const status = await api<SessionStatus>(sendActivity ? "/auth/activity" : "/auth/session", {
        cache: "no-store",
        signal: AbortSignal.timeout(5_000),
        ...(sendActivity ? { method: "POST", body: JSON.stringify({ clientId }) } : {}),
      });
      if (stopped) return;
      if (status.accountId !== accountId || (clientId && status.clientId !== clientId)) {
        stopped = true;
        callbacks.changed();
        return;
      }
      clientId = status.clientId;
      if (confirmedClientId !== clientId) {
        confirmedClientId = clientId;
        for (const listener of referenceListeners) listener(clientId);
      }
      // Only server-confirmed activity moves the deadline; resuming also revalidates with the server.
      deadline = startedAt + status.expiresAt - status.serverNow;
      if (sendActivity) { sentRevision = revision; signalTabs(); }
      callbacks.error("");
      displayWarning();
    } catch (error) {
      if (stopped) return;
      if (error instanceof ApiError && error.status === 409) {
        stopped = true;
        callbacks.changed();
      } else if (error instanceof ApiError && error.status === 401) {
        if (error.code === "SESSION_IDLE_TIMEOUT" || (deadline && performance.now() >= deadline)) expire();
        else stopped = true; // The API's central unauthorized handler already clears the account.
      } else if (deadline && performance.now() >= deadline) expire();
      else callbacks.error("Deine Anmeldung konnte nicht geprüft oder verlängert werden. Prüfe deine Verbindung.");
    } finally { inFlight = false; }
  }

  function displayWarning() {
    if (!deadline) return;
    const seconds = Math.max(0, Math.ceil((deadline - performance.now()) / 1_000));
    callbacks.warning(seconds <= 60 ? seconds : null);
  }

  function tick() {
    if (stopped) return;
    displayWarning();
    const now = performance.now();
    if (deadline && now >= deadline) void check();
    else if (inputRevision > sentRevision && now - lastActivitySent >= 10_000) void check(true);
    else if (now - lastCheck >= 30_000) void check();
  }

  function record(event: Event) {
    if (!event.isTrusted || document.visibilityState !== "visible" || stopped) return;
    inputRevision += 1;
    tick();
  }

  function visible() {
    if (document.visibilityState === "visible") { displayWarning(); void check(); }
  }

  function storage(event: StorageEvent) {
    // Storage carries only a wake-up signal; deadlines always come from the server.
    if (event.key === activitySignalKey) void check();
  }

  for (const name of inputEvents) document.addEventListener(name, record, { capture: true, passive: true });
  document.addEventListener("visibilitychange", visible);
  window.addEventListener("focus", visible);
  window.addEventListener("pageshow", visible);
  window.addEventListener("storage", storage);
  const timer = window.setInterval(tick, 1_000);
  void check();

  return {
    staySignedIn: () => {
      if (stopped || document.visibilityState !== "visible") return;
      if (inFlight && inputRevision > sentRevision) return;
      inputRevision += 1;
      void check(true);
    },
    stop: () => {
      if (confirmedClientId === clientId) confirmedClientId = "";
      stopped = true;
      window.clearInterval(timer);
      for (const name of inputEvents) document.removeEventListener(name, record, true);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("focus", visible);
      window.removeEventListener("pageshow", visible);
      window.removeEventListener("storage", storage);
    },
  };
}
